import FormField from "@components/Form/FormFields/FormFields";
import FormErrorMessage from "@components/FormErrorMessage/FormErrorMessage";
import Grid from "@components/Grid/Grid";
import SubmitButton from "@components/SubmitButton/SubmitButton";
import { getBusinessUnit } from "@utilities/getBusinessUnit";
import setGADataLayer from "@utilities/setGADataLayer";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ALUMNIEMAIL_URL } from "../../constants";
import styles from "./C45_AlumniJobBoard.module.scss";

export interface UserContactDetailsProps {
  formTitle: string;
  emailLabelText: string;
  emailHintText?: string;
  titleLabelText: string;
  firstNameLabelText: string;
  lastNameLabelText: string;
  schoolLabelText: string;
  schoolList: string[];
  programmeLabelText: string;
  graduationYearLabelText: string;
  contactEmailLabelText: string;
  contactEmailHintText?: string;
  formBottomDescriptionText?: string;
  mailTo: string;
  successPage: {
    fields: {
      slug: string;
    };
  };
}

let firstInteract = 0;

export const C45_AlumniJobBoard = (props: {
  content: UserContactDetailsProps;
}) => {
  const { content } = props;
  const {
    formTitle,
    emailLabelText,
    emailHintText,
    firstNameLabelText,
    lastNameLabelText,
    schoolLabelText,
    schoolList,
    programmeLabelText,
    graduationYearLabelText,
    contactEmailLabelText,
    contactEmailHintText,
    formBottomDescriptionText,
    successPage,
  } = content;

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState("");

  const { register, handleSubmit, formState, watch } = useForm({
    mode: "onBlur",
  });

  const router = useRouter();

  useEffect(() => {
    const subscription = watch((_e, a) => {
      if (a.type === "change" && firstInteract < 1) {
        setGADataLayer({
          event: "formStart",
          formName: "AlumniJobBoard",
        });
        firstInteract++;
        return () => subscription.unsubscribe();
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const FormFields = [
    {
      propertyName: "emailAddress",
      validation: {
        isRequired: true,
        email: true,
        maxLength: 100,
      },
      labelText: emailLabelText,
      hintText: emailHintText,
    },
    {
      propertyName: "firstName",
      validation: {
        isRequired: true,
        maxLength: 30,
        firstName: true,
      },
      labelText: firstNameLabelText,
    },
    {
      propertyName: "lastName",
      validation: {
        isRequired: true,
        maxLength: 30,
        lastName: true,
      },
      labelText: lastNameLabelText,
    },
    {
      propertyName: "school",
      validation: {
        isRequired: true,
      },
      labelText: schoolLabelText,
      options: schoolList?.map((school) => ({ id: school, value: school })),
      formType: "select",
      isEmptyFirstOption: true,
    },
    {
      propertyName: "programme",
      validation: {
        isRequired: true,
        maxLength: 30,
      },
      labelText: programmeLabelText,
    },
    {
      propertyName: "graduationYear",
      validation: {
        isRequired: true,
        minLength: 4,
        maxLength: 4,
        integerValidator: true,
      },
      labelText: graduationYearLabelText,
    },
    {
      propertyName: "contactEmailAddress",
      validation: {
        isRequired: true,
        email: true,
        maxLength: 100,
      },
      labelText: contactEmailLabelText,
      hintText: contactEmailHintText,
    },
  ];

  const redirectToSuccessfulPage = async () => {
    setGADataLayer({
      event: "formComplete",
      formName: "AlumniJobBoard",
      businessUnit: getBusinessUnit(),
      programmeCode: "",
      eventType: "",
    });
    const successPageUrl = successPage?.fields?.slug ?? "/404";
    console.log("successPageUrl", successPageUrl);
    await router.push(successPageUrl);
  };

  const onSubmit = async (data) => {
    setError("");
    setLoading(true);

    try {
      const response = await fetch(ALUMNIEMAIL_URL, {
        method: "POST",
        body: JSON.stringify({
          contactEmailAddress: data.contactEmailAddress,
          emailAddress: data.emailAddress,
          firstName: data.firstName,
          lastName: data.lastName,
          graduationYear: data.graduationYear,
          programme: data.programme,
          school: data.school,
          mailTo: content.mailTo,
        }),
      });

      if (response.ok) {
        await redirectToSuccessfulPage();
      } else {
        setError("Something went wrong");
      }
    } catch (_e) {
      setError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const renderRegularFormFields = (field, index) => {
    return (
      <FormField
        formType={field?.formType}
        watch={watch}
        validation={field?.validation}
        type={field.type ? field.type : "text"}
        register={register}
        property={field.dataText}
        key={`${field?.labelText}${index}`}
        hintText={field?.hintText}
        isEmptyFirstOption={field?.isEmptyFirstOption}
        placeholder={field.labelText}
        options={field?.options}
        name={field?.propertyName}
        errors={formState.errors}
      />
    );
  };

  return (
    <div className="wrapper">
      <Grid row>
        <Grid column sm={12} md={6}>
          <div className={clsx(styles.component, "component", styles.form)}>
            <h1 className="h2">{formTitle}</h1>
          </div>
          <section
            className={clsx(
              styles.component,
              styles["job-board"],
              styles["form"],
              "form",
              styles.cf
            )}
          >
            <form method="POST" className="form" onSubmit={handleSubmit(onSubmit)}>
              <div className={styles["fields-floated"]}>
                <div className="form-row">
                  <section className={styles["form-group-wrapper"]}>
                    {error && <FormErrorMessage key={"error"} text={error} />}
                    {FormFields.map((field, index) => {
                      return renderRegularFormFields(field, index);
                    })}
                  </section>
                </div>
                <p
                  dangerouslySetInnerHTML={{
                    __html: formBottomDescriptionText ?? "",
                  }}
                />
                <p>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      window.scrollTo(0, 0);
                    }}
                  >
                    <u>Privacy statement</u>
                  </a>
                </p>

                <div className={styles["btn-and-text-wrapper"]}>
                  <SubmitButton
                    loading={loading}
                    id="submitButton"
                    text="Submit"
                  />
                </div>
              </div>
            </form>
          </section>
        </Grid>
      </Grid>
    </div>
  );
};

export default C45_AlumniJobBoard;
